import type { ExtendedOptions, RequestOptions } from '@cdc-internal/fe-toolbox/libs/fetcher'
import { queryOptions, useSuspenseQuery } from '@tanstack/react-query'

import { useSsrContext } from '@/contexts/ssr'
import { defaultNextjsFetchCacheOptions } from '@/libs/fetcher/configs'
import {
  coinsaiSunshineTokenFetcher,
  marketfeedFetcher,
  marketFetcher,
  priceFetcher,
} from '@/libs/fetcher/presets'
import { CoinMarket, CoinStatistics, TopMoverItem } from '@/types/coin'
import { MarketPair } from '@/types/resource'
import { isObject } from '@/utils'

export const formatSlugForApi = (slug: string): string => {
  return slug.replace(/\s+/g, '-').toLowerCase()
}

export const fetchCoinMarketBySlug = (
  slug: string,
  requestOptions?: RequestOptions,
  silent404 = false,
) => {
  const apiSlug = formatSlugForApi(slug)

  return priceFetcher<CoinMarket>(`/token-price/${apiSlug}`, requestOptions, {
    customData: { silent404 },
  }).then(res => {
    // Clear unused prices to save memory
    if (isObject(res)) {
      res.prices = []
    }
    return res
  })
}

export const coinMarketQueryOptions = (slug: string, silent404 = false) =>
  queryOptions({
    queryKey: ['coinMarket', slug, silent404],
    queryFn: () => fetchCoinMarketBySlug(slug, undefined, silent404),
    refetchOnMount: false,
  })

export const useCoinMarket = (slug: string, silent404 = false) =>
  useSuspenseQuery(coinMarketQueryOptions(slug, silent404))

export interface CoinRankPair {
  slug: string
  rank: number
}
export const fetchAllRanks = (options?: ExtendedOptions<CoinRankPair[]>) =>
  priceFetcher<CoinRankPair[]>('/all-ranks', undefined, options)

export const fetchCoinMarketPair = (slug: string) =>
  marketFetcher<MarketPair[]>(`/token/${slug}/market-pairs`)
export const coinMarketPairQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: ['coinMarketPair', slug],
    queryFn: () => fetchCoinMarketPair(slug),
  })

export const useCoinMarketPair = (slug: string) =>
  useSuspenseQuery(coinMarketPairQueryOptions(slug))

export const fetchMarketCdcPair = (slug: string) =>
  marketFetcher<MarketPair>(`/token/${slug}/cdc-pair`)
export const marketCdcPairQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: ['marketCdcPair', slug],
    queryFn: () => fetchMarketCdcPair(slug),
  })

export const useMarketCdcPair = (slug: string) => useSuspenseQuery(marketCdcPairQueryOptions(slug))

export const fetchCoinStatistics = (slug: string, currency?: string) =>
  priceFetcher<CoinStatistics>(`/statistics/${slug}?convert=${currency}`)

export const coinStatisticsQueryOptions = (slug: string, currency?: string) =>
  queryOptions({
    queryKey: ['coinStatistics', slug, currency],
    queryFn: () => fetchCoinStatistics(slug, currency),
    // Return empty statistics object if API returns null
    select: data => data || { statistics: [] },
  })

export const useCoinStatistics = (slug: string, currency?: string) => {
  const { selectedCurrency } = useSsrContext()
  const finalCurrency = currency || selectedCurrency
  return useSuspenseQuery(coinStatisticsQueryOptions(slug, finalCurrency))
}
export interface GlobalMetrics {
  coins: number
  market_cap: number
  market_cap_change_rate: number
  volume_24h: number
  btc_dominance_rate: number
  eth_dominance_rate: number
  eth_gas: number
}

export interface FearGreedItem {
  id: string
  type: string
  labels: unknown[]
  title: string
  summary: string
  statistic_details: {
    value: number
    key: string
    type: string
    change_pct?: number
    meta_data: {
      source: number
      text?: string
      currency?: string
      slug?: string
      all_data?: Array<{
        slug: string
        value: number
      }>
    }
    update_timestamp: number
  }
}

export type FearGreedData = FearGreedItem[]

// https://mcoproduct.atlassian.net/wiki/spaces/C/pages/1373798547/Token+Price+REST+WebSocket+API#/global-metrics
export const fetchGlobalMetrics = (
  extendedOptions?: ExtendedOptions<GlobalMetrics>,
  enableServerCache = true,
) =>
  priceFetcher<GlobalMetrics>(
    '/global-metrics',
    enableServerCache ? defaultNextjsFetchCacheOptions : undefined,
    extendedOptions,
  )

export const globalMetricsQueryOptions = (extendedOptions?: ExtendedOptions<GlobalMetrics>) =>
  queryOptions({
    queryKey: ['globalMetrics', extendedOptions],
    queryFn: () => fetchGlobalMetrics(extendedOptions),
  })

export const useGlobalMetrics = (extendedOptions?: ExtendedOptions<GlobalMetrics>) =>
  useSuspenseQuery(globalMetricsQueryOptions(extendedOptions))

// Fear & Greed Index API
export const fetchFearGreedIndex = (extendedOptions?: ExtendedOptions<FearGreedData>) =>
  marketfeedFetcher<FearGreedData>(
    '/public/latest?types=statistic',
    defaultNextjsFetchCacheOptions,
    extendedOptions,
  )

export const fearGreedIndexQueryOptions = (extendedOptions?: ExtendedOptions<FearGreedData>) =>
  queryOptions({
    queryKey: ['fearGreedIndex', extendedOptions],
    queryFn: () => fetchFearGreedIndex(extendedOptions),
  })

export const useFearGreedIndex = (extendedOptions?: ExtendedOptions<FearGreedData>) =>
  useSuspenseQuery(fearGreedIndexQueryOptions(extendedOptions))

// Market types colocated with services
export interface BuzzingOnXMentions {
  '24_hr_change': number
  count: number
}

export interface BuzzingOnXScores {
  '24_hr_change': number
  score: number
}

export interface BuzzingOnXItem {
  CA: string | null
  categories: string | null
  chain: string | null
  coin_info_id: number | string | null
  is_mainapp_tradable: boolean | null
  market_cap: number | null
  mentions: BuzzingOnXMentions
  mindshares: BuzzingOnXScores
  name: string
  rails_id: number | string | null
  sentiment_score: BuzzingOnXScores
  symbol: string
}

export interface BuzzingOnXData {
  social_ranking: BuzzingOnXItem[]
}

export interface HotCoinItem {
  name: string
  slug: string
  symbol: string
  token_id: number
  usd_price_change_24h: number
}

export type HotCoinsData = HotCoinItem[]

export enum TrendingCardType {
  HotTokens = 'hotTokens',
  BuzzingX = 'buzzingX',
  TopMovers = 'topMovers',
  NewlyListed = 'newlyListed',
}

export type TopMoversData = TopMoverItem[]

// Buzzing on X API
export const fetchBuzzingOnX = (
  query?: Record<string, any>,
  extendedOptions?: ExtendedOptions<BuzzingOnXData>,
) =>
  coinsaiSunshineTokenFetcher<BuzzingOnXData>(
    '/public/token-social-ranking',
    {
      ...defaultNextjsFetchCacheOptions,
      queries: query,
    },
    extendedOptions,
  )

// Hot Coins API
export const fetchHotCoins = (
  query?: Record<string, any>,
  extendedOptions?: ExtendedOptions<HotCoinsData>,
) =>
  priceFetcher<HotCoinsData>(
    '/trending-tokens',
    {
      ...defaultNextjsFetchCacheOptions,
      queries: query,
    },
    extendedOptions,
  )
